import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, LogIn } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { PasswordField } from '@shared/auth/components/PasswordField';
import { useRememberMe } from '@shared/auth/hooks/useRememberMe';
import { useAuth } from '@shared/hooks/useAuth';
import { useAppConfig } from '@shared/app/app-config';
import { isApiError } from '@shared/api';
import { PATHS } from '@shared/router/paths';

/**
 * identity-service `LoginDto`: `email` (IsEmail) and `password` (MinLength 8).
 * The form only requires a password; the length rule is the backend's to
 * enforce and report, so a sign-in screen never hints at password policy.
 */
const schema = z.object({
  email: z.string().trim().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean(),
});
type LoginForm = z.infer<typeof schema>;

const REASON_MESSAGES: Record<string, string> = {
  expired: 'Your session has ended. Please sign in again.',
  'signed-out-elsewhere': 'You were signed out in another tab.',
  'password-changed': 'Your password was changed. Please sign in with the new password.',
};

/**
 * Sign-in for every application. There is one authentication system —
 * identity-service — and this form is its only client. What the account may
 * open afterwards is decided by its application access, checked by the
 * application guard (and, finally, by every backend request).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { name, tagline } = useAppConfig();
  const { login, isLoggingIn } = useAuth();
  const { remembered, save } = useRememberMe();
  const params = new URLSearchParams(location.search);
  const addingAccount = params.get('add') === '1';
  const prefill = params.get('email') ?? '';
  const reason = params.get('reason');
  const form = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: addingAccount ? prefill : remembered.email,
      password: '',
      rememberMe: addingAccount ? false : remembered.rememberMe,
    },
  });

  const onSubmit = async (values: LoginForm) => {
    try {
      await login({ email: values.email, password: values.password });
      if (!addingAccount) save(values.email, values.rememberMe);
      toast.success(addingAccount ? 'Account added' : 'Signed in');
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(addingAccount ? PATHS.root : (from ?? PATHS.root), { replace: true });
    } catch (error) {
      // One generic message from the backend ("Invalid credentials") — never
      // reveals which check failed. Lockout and rate limits arrive the same way.
      const message =
        isApiError(error) && error.isRateLimited
          ? error.message
          : isApiError(error) && error.status === 401
            ? 'The email or password is incorrect, or the account is locked or inactive.'
            : error instanceof Error
              ? error.message
              : 'Unable to sign in. Please try again.';
      form.setError('root', { message });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold">
          {addingAccount ? 'Add account' : `Sign in to ${name}`}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {addingAccount
            ? 'Sign in to the account you want to add. Your current account stays signed in.'
            : tagline}
        </p>
      </div>
      {reason && REASON_MESSAGES[reason] && !rootError ? (
        <p className="mb-4 rounded-md border border-info/30 bg-info/10 px-3 py-2 text-sm text-foreground">
          {REASON_MESSAGES[reason]}
        </p>
      ) : null}
      {rootError ? (
        <p
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {rootError}
        </p>
      ) : null}
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-invalid={Boolean(form.formState.errors.email)}
            {...form.register('email')}
          />
          {form.formState.errors.email ? (
            <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
          ) : null}
        </div>
        <PasswordField
          id="password"
          label="Password"
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 rounded" {...form.register('rememberMe')} />
            Remember my email
          </label>
          <Link
            to={PATHS.auth.forgotPassword}
            className="text-sm font-medium text-primary hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <button
          type="submit"
          disabled={isLoggingIn}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {isLoggingIn ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />}
          Sign in
        </button>
      </form>
    </div>
  );
}
